import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Typography, Paper, Box, Button } from "@mui/material";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";

import * as styles from "../BackofficeRequest/Styles.js";
import { Spaces } from "../../assets/Variables/SpaceVariables.js";
import * as TextVariables from "../../assets/Variables/TextVariables.js";
import Colors from "../../assets/Variables/ColorsVariables.js";

import MoneyCard from "../../components/Cards/MoneyCard.js";
import TitledInput from "../../components/Inputs/TitledInput.js";
import NotificationModal from "../../components/Notifications/NotificationModal.js";

import { useCookies } from "../../Tools/CookieChecker.js";
import { BalanceFormatter } from "../../components/CurrencyIcon/CurrencyIcon.js";

import IBTransferRequest from "../../Endpoints/IBTransferRequest";
import IBInformationRequest from "../../Endpoints/IBInformationRequest";
import WalletRequest from "../../Endpoints/WalletRequest.js";

const toNumber = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : NaN;
};

function TransferIBsRequestPage() {
  const navigate = useNavigate();
  const { currency } = useCookies();

  const [submittedOnce, setSubmittedOnce] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [modalProps, setModalProps] = useState({
    iconType: "",
    title: "",
    message: "",
    onAcceptLabel: undefined,
    onRefuseLabel: undefined,
    onAccept: undefined,
    onRefuse: undefined,
  });

  const [submitting, setSubmitting] = useState(false);
  const [wallet, setWallet] = useState({ amount: 0, currency: "" });
  const [loadingWallet, setLoadingWallet] = useState(false);

  const [formValues, setFormValues] = useState({
    targetEmail: "",
    amount: "",
    comment: "",
  });

  const [targetInfo, setTargetInfo] = useState(null);

  useEffect(() => {
    RequestWalletData();
  }, []);

  const RequestWalletData = async () => {
    try {
      setLoadingWallet(true);
      const response = await WalletRequest();
      const w = response?.data?.data?.wallet;

      if (w) {
        setWallet({
          amount: w.balance ?? 0,
          currency: w.currency ?? "",
        });
      } else {
        setWallet({ amount: 0, currency: "" });
      }
    } catch (error) {
      console.log(error);
      setWallet({ amount: 0, currency: "" });
    } finally {
      setLoadingWallet(false);
    }
  };

  const handleClickToBackPage = () => {
    navigate("/requests");
  };

  const withdrawable = Number(wallet.amount || 0);
  const walletCurrency = wallet.currency || currency || "USD";

  const errors = useMemo(() => {
    const e = {
      targetEmail: false,
      amount: false,
      comment: false,
    };

    if (!formValues.targetEmail.trim()) e.targetEmail = true;

    const n = toNumber(formValues.amount);
    if (!formValues.amount.toString().trim()) e.amount = true;
    else if (!Number.isFinite(n) || n <= 0) e.amount = true;
    else if (Number.isFinite(withdrawable) && n > withdrawable) e.amount = true;

    e.comment = false;
    return e;
  }, [formValues, withdrawable]);

  const canSubmit =
    !errors.targetEmail &&
    !errors.amount &&
    !submitting &&
    !loadingWallet &&
    formValues.targetEmail.trim() &&
    formValues.amount.toString().trim();

  const handleChange = (key) => (event) => {
    let v = event.target.value;

    if (key === "amount") v = v.replace(/[^0-9.]/g, "").slice(0, 20);
    if (key === "targetEmail") v = v.slice(0, 120);
    if (key === "comment") v = v.slice(0, 250);

    setFormValues((p) => ({ ...p, [key]: v }));
  };

  const handleCloseNotification = () => setShowNotification(false);

  const showWarn = (title, message) => {
    setModalProps({
      iconType: "warning",
      title,
      message,
      onAcceptLabel: "Close",
      onRefuseLabel: undefined,
      onAccept: () => setShowNotification(false),
      onRefuse: undefined,
    });
    setShowNotification(true);
  };

  const showOk = (result) => {
    const msg =
      result && typeof result === "object"
        ? `satisfactory transfer\n\nFrom: ${result.emailfrom || "-"} (${
            result.currencyfrom || "-"
          })\nTo: ${result.emailto || "-"} (${result.currencyto || "-"})\nAmount: ${
            result.amount ?? "-"
          }`
        : "Your IB transfer request was submitted successfully.";

    setModalProps({
      iconType: "success",
      title: "Request sent",
      message: msg,
      onAcceptLabel: "Continue",
      onRefuseLabel: undefined,
      onAccept: () => navigate("/requests"),
      onRefuse: undefined,
    });
    setShowNotification(true);
  };

  const buildConfirmMessage = (info) => {
    const email = formValues.targetEmail.trim();
    const name = info?.name || "-";
    const targetCurrency = info?.mainWallet?.currency || "-";
    const amount = Number(formValues.amount || 0).toFixed(2);
    const comment = (formValues.comment || "").trim();

    return (
      `Recipient Email: ${email}\n` +
      `Recipient Name: ${name}\n` +
      `Amount Sent: ${amount} ${currency}\n` +
      `Recipient Will Receive In: ${targetCurrency}\n` +
      `Comment: ${comment || "-"}`
    );
  };

  const handleConfirmTransfer = async () => {
    try {
      setSubmitting(true);

      const emailto = formValues.targetEmail.trim();
      const amount = Number(formValues.amount);

      const res = await IBTransferRequest.ibTransfer({ emailto, amount });
      const result = res?.data?.data;

    setFormValues({ targetEmail: "", amount: "", comment: "" });
setTargetInfo(null);
setSubmittedOnce(false);
RequestWalletData();
showOk(result);
    } catch (error) {
      console.error(error);

      const backendMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.response?.data?.data?.message ||
        error?.response?.data?.data?.error ||
        "Transfer failed. Please try later.";

      showWarn("Error", backendMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    setSubmittedOnce(true);

    if (!canSubmit) {
      showWarn("Incomplete fields", "Please fill in the missing fields.");
      return;
    }

    try {
      setSubmitting(true);

      const email = formValues.targetEmail.trim();
      const res = await IBInformationRequest.information(email);
      const info = res?.data?.data;

      if (!info) {
        const backendMessage =
          res?.data?.message ||
          res?.data?.data?.message ||
          "User does not exist in the server. Please verify your information";

        setModalProps({
          iconType: "warning",
          title: "Problem with request",
          message: backendMessage,
          onAcceptLabel: "Try again",
          onAccept: () => setShowNotification(false),
        });
        setShowNotification(true);
        return;
      }

      setTargetInfo(info);

      setModalProps({
        iconType: "warning",
        title: "Please review the information before continuing",
        message: buildConfirmMessage(info),
        onRefuseLabel: "Close",
        onAcceptLabel: "Confirm Transfer",
        onRefuse: () => setShowNotification(false),
        onAccept: () => handleConfirmTransfer(),
      });

      setShowNotification(true);
    } catch (error) {
      console.error(error);

      if (error?.response) {
        const backendMessage =
          error.response.data?.message ||
          error.response.data?.error ||
          error.response.data?.data?.message ||
          error.response.data?.data?.error ||
          "Please try later";

        if (error.response.status === 400) {
          setModalProps({
            iconType: "error",
            title: "Problem with request",
            message: backendMessage,
            onAcceptLabel: "Try again",
            onAccept: () => setShowNotification(false),
          });
        } else {
          setModalProps({
            iconType: "error",
            title: "Problem with service",
            message:
              "The following error has been caused: " +
              backendMessage +
              ". please contact an administrator",
            onAcceptLabel: "Try again",
            onAccept: () => setShowNotification(false),
          });
        }
      } else {
        setModalProps({
          iconType: "error",
          title: "Problem with service",
          message: "Please contact an administrator - " + error?.message,
          onAcceptLabel: "Accept",
          onAccept: () => setShowNotification(false),
        });
      }

      setShowNotification(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={styles.mainBox}>
      <NotificationModal
        open={showNotification}
        onClose={handleCloseNotification}
        iconType={modalProps.iconType}
        title={modalProps.title}
        message={modalProps.message}
        onAcceptLabel={modalProps.onAcceptLabel}
        onRefuseLabel={modalProps.onRefuseLabel}
        onAccept={modalProps.onAccept}
        onRefuse={modalProps.onRefuse}
      />

      <Box sx={styles.secondBox}>
        <Box sx={styles.backButtonContainer}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Button
              onClick={handleClickToBackPage}
              startIcon={<ArrowBackIosIcon style={{ fontSize: 10 }} />}
              sx={{ textTransform: "none", color: "black" }}
            >
              Go back
            </Button>
          </Box>
        </Box>

        <Paper
          elevation={2}
          sx={{
            ...(styles.mainPaper || {}),
            borderRadius: "12px",
            padding: Spaces.Size_5,
            maxWidth: 920,
            margin: "0 auto",
          }}
        >
          <Typography
            sx={{
              fontFamily: TextVariables.Body_3_Bold,
              marginBottom: Spaces.Size_4,
            }}
          >
            IBs Transfer Request
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "320px 1fr" },
              gap: Spaces.Size_5,
              alignItems: "start",
              width: "100%",
            }}
          >
            <Box sx={{ maxWidth: 320 }}>
              <MoneyCard
                title="Withdrawable amount"
                value={BalanceFormatter.round(withdrawable, walletCurrency)}
                asset={walletCurrency}
              />
            </Box>

            <Box sx={{ width: "100%" }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: Spaces.Size_3,
                }}
              >
                <TitledInput
                  name="Email of Target IB"
                  title="Email of Target IB"
                  value={formValues.targetEmail}
                  placeholder={"Enter the destination IB Email"}
                  handleChange={handleChange("targetEmail")}
                  type="text"
                  error={submittedOnce && errors.targetEmail}
                  helperText={
                    submittedOnce && errors.targetEmail ? (
                      <Box sx={styles.emptyFieldLabel}>
                        <span
                          className="material-symbols-outlined"
                          style={{ fontSize: Spaces.Size_3, color: "red" }}
                        >
                          error
                        </span>
                        <Typography
                          sx={{
                            fontFamily: TextVariables.Caption_1,
                            color: Colors.Error_700,
                          }}
                        >
                          Please, enter an email
                        </Typography>
                      </Box>
                    ) : (
                      ""
                    )
                  }
                />

                <TitledInput
                  name="Amount"
                  title="Amount"
                  value={formValues.amount}
                  placeholder={"Enter the amount to transfer"}
                  handleChange={handleChange("amount")}
                  type="number"
                  error={submittedOnce && errors.amount}
                  helperText={
                    submittedOnce && errors.amount ? (
                      <Box sx={styles.emptyFieldLabel}>
                        <span
                          className="material-symbols-outlined"
                          style={{ fontSize: Spaces.Size_3, color: "red" }}
                        >
                          error
                        </span>
                        <Typography
                          sx={{
                            fontFamily: TextVariables.Caption_1,
                            color: Colors.Error_700,
                          }}
                        >
                          {Number(formValues.amount || 0) > withdrawable
                            ? "Amount exceeds withdrawable balance"
                            : "Please, enter an amount"}
                        </Typography>
                      </Box>
                    ) : (
                      ""
                    )
                  }
                />

                <TitledInput
                  name="Comment"
                  title="Comment"
                  value={formValues.comment}
                  placeholder={"Enter a comment"}
                  handleChange={handleChange("comment")}
                  type="text"
                  error={false}
                  helperText={" "}
                />
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  marginTop: Spaces.Size_3,
                }}
              >
                <Button
                  onClick={handleSubmit}
                  disabled={submitting || loadingWallet}
                  sx={{
                    textTransform: "none",
                    borderRadius: "8px",
                    fontWeight: 700,
                    padding: "8px 14px",
                    fontSize: "12px",
                    minWidth: 140,
                    backgroundColor: canSubmit ? Colors.Primary_600 : "#E5E7EB",
                    color: canSubmit ? "#fff" : "#6B7280",
                    "&:hover": {
                      backgroundColor: canSubmit ? Colors.Primary_700 : "#E5E7EB",
                    },
                  }}
                >
                  {loadingWallet
                    ? "Loading..."
                    : submitting
                    ? "Submitting..."
                    : "Submit request"}
                </Button>
              </Box>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}

export default TransferIBsRequestPage;